import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './signup.module.css'
import { api } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'

export const Route = createFileRoute('/signup')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: SignupPage,
})

interface FormErrors {
  name?: string
  email?: string
  password?: string
  confirmPassword?: string
}

function SignupPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = 'Password must contain at least one uppercase letter'
    } else if (!/[a-z]/.test(password)) {
      newErrors.password = 'Password must contain at least one lowercase letter'
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = 'Password must contain at least one number'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setApiError(null)

    if (!validateForm()) return

    setIsLoading(true)

    api
      .signup({
        email,
        password,
        passwordConfirmation: confirmPassword,
        ...(name ? { name } : {}),
      })
      .then(() => {
        setVerificationSent(true)
      })
      .catch((err: unknown) => {
        setApiError(err instanceof Error ? err.message : 'Signup failed')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (verificationSent) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <CardBody>
            <div className={styles.logo}>
              <span className={styles.logoIcon}>&#10024;</span>
              <span className={styles.logoText}>ProofPulse</span>
            </div>
            <h2 className={styles.title}>Verify your email</h2>
            <p className={styles.subtitle}>
              We&apos;ve sent a verification link to <strong>{email}</strong>. Please check your
              inbox and click the link to verify your account.
            </p>
            <Alert variant="success" className={styles.alert}>
              Your account has been created! Please verify your email before signing in.
            </Alert>
            <div className={styles.actions}>
              <Link to="/login">
                <Button variant="primary" className={styles.fullWidth}>
                  Go to Login
                </Button>
              </Link>
            </div>
            <p className={styles.footer}>
              Didn&apos;t receive the email?{' '}
              <button
                type="button"
                onClick={() => {
                  // TODO: Implement resend verification
                }}
                className={styles.resendButton}
              >
                Resend
              </button>
            </p>
          </CardBody>
        </Card>
      </main>
    )
  }

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>&#10024;</span>
            <span className={styles.logoText}>ProofPulse</span>
          </div>
          <h2 className={styles.title}>Create your account</h2>
          <p className={styles.subtitle}>Start collecting testimonials in minutes</p>

          {apiError && (
            <Alert variant="danger" className={styles.alert}>
              {apiError}
            </Alert>
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              autoComplete="name"
              {...(errors.name && { error: errors.name })}
            />

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              {...(errors.email && { error: errors.email })}
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              {...(errors.password && { error: errors.password })}
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              autoComplete="new-password"
              {...(errors.confirmPassword && { error: errors.confirmPassword })}
              required
            />

            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                className={styles.fullWidth}
              >
                Create account
              </Button>
            </div>
          </form>

          <p className={styles.footer}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>

          <p className={styles.terms}>
            By signing up, you agree to our{' '}
            <a href="/terms" className={styles.link}>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className={styles.link}>
              Privacy Policy
            </a>
            .
          </p>
        </CardBody>
      </Card>
    </main>
  )
}
